/**
 * H5PEmbedPlayerPage
 * Embeddable H5P player page for iframe loading
 *
 * Supports two modes:
 * 1. postMessage mode (default) - Communicates with parent window via postMessage
 * 2. API mode - When baseUrl and publicKey are provided, can load content directly from API
 *
 * URL params:
 * - ?metaId=content-v1+tenant+source@library@typeId+contentId - Load content by meta ID (preferred)
 * - ?contentId=xxx - Load content by ID (fallback if metaId not provided)
 * - ?baseUrl=https://api.example.com - API base URL (optional if VITE_API_URL is set)
 * - ?h5pUrl=https://h5p.example.com - H5P server URL (optional if VITE_H5P_URL is set)
 * - ?publicKey=pub_xxx - Public API key for authentication (read-only access, format: pub_xxxxx)
 * - ?hideSubmitButton=true - Inject CSS to hide H5P `.h5p-question-check-answer` button
 *
 * Authentication:
 * - publicKey is required for API mode (view operations)
 *
 * Usage:
 * - View: ?metaId=xxx&publicKey=pub_xxx OR ?contentId=xxx&publicKey=pub_xxx
 *
 * Parent Messages (send to iframe):
 * - H5P_PLAYER_INIT: Initialize player with content
 * - H5P_PLAYER_RESET: Reset player state
 * - H5P_PLAYER_CONFIG_UPDATE: Update player config
 *
 * Iframe Messages (sent to parent):
 * - H5P_PLAYER_READY: Player is ready to receive messages
 * - H5P_PLAYER_LOADED: Player content has loaded
 * - H5P_PLAYER_ANSWER: User answered a question
 * - H5P_PLAYER_SUBMIT: User submitted answers
 * - H5P_PLAYER_COMPLETE: Content completed
 * - H5P_PLAYER_HEIGHT_CHANGE: Iframe height changed
 * - H5P_PLAYER_ERROR: Error occurred
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { H5PPlayer, type H5PContentItem } from '@ai-content-hub/shared-components';
import { normalizeContentLocale } from '@ai-content-hub/content-core';
import {
    configureApi,
    fetchContent,
    contentService,
    extractH5PContentFromData,
} from '@ai-content-hub/shared-api';
import { postToParent, isTrustedMessage } from '../../lib/embedOrigins';

// Message types for H5P Player
const H5P_PLAYER_MESSAGE_TYPES = {
    // From parent to iframe
    INIT: 'H5P_PLAYER_INIT',
    RESET: 'H5P_PLAYER_RESET',
    CONFIG_UPDATE: 'H5P_PLAYER_CONFIG_UPDATE',
    // From iframe to parent
    READY: 'H5P_PLAYER_READY',
    LOADED: 'H5P_PLAYER_LOADED',
    ANSWER: 'H5P_PLAYER_ANSWER',
    SUBMIT: 'H5P_PLAYER_SUBMIT',
    COMPLETE: 'H5P_PLAYER_COMPLETE',
    HEIGHT_CHANGE: 'H5P_PLAYER_HEIGHT_CHANGE',
    ERROR: 'H5P_PLAYER_ERROR',
} as const;

interface H5PPlayerInitPayload {
    content?: H5PContentItem;
    library?: string;
    metaId?: string;
    contentId?: string;
    readOnly?: boolean;
    apiConfig?: {
        baseUrl?: string;
        h5pUrl?: string;
        publicKey?: string;
    };
}

interface ParentMessage {
    type: string;
    contentId?: string;
    payload?: H5PPlayerInitPayload;
}

const sendToParent = (type: string, contentId: string, payload?: unknown) => {
    if (window.parent !== window) {
        postToParent({ source: 'h5p-player', type, contentId, payload });
    }
};

export const H5PEmbedPlayerPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [content, setContent] = useState<H5PContentItem | null>(null);
    const [library, setLibrary] = useState<string | undefined>(undefined);
    const [readOnly, setReadOnly] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [apiMode, setApiMode] = useState(false);
    const [key, setKey] = useState(0); // Key for forcing re-render on reset

    // URL params
    const urlLibrary = searchParams.get('library');
    const urlMetaId = searchParams.get('metaId');
    const urlContentId = searchParams.get('contentId');
    const urlBaseUrl = searchParams.get('baseUrl') || import.meta.env.VITE_API_URL || '';
    // Default h5pUrl to baseUrl if not provided (replace /api with /h5p if present)
    const defaultH5pUrl = urlBaseUrl ? urlBaseUrl.replace(/\/api\/?$/, '/h5p') : '';
    const urlH5pUrl = searchParams.get('h5pUrl') || import.meta.env.VITE_H5P_URL || defaultH5pUrl;
    const urlPublicKey = searchParams.get('publicKey') || '';
    /** When true, inject CSS to hide the H5P "Check" / "Check answer" button. */
    const hideSubmitButton = searchParams.get('hideSubmitButton') === 'true';
    // EAA-1029: H5P core chrome (Fullscreen, Check, Retry, …) rendered English no
    // matter the host page's locale — same `?lang=` convention as
    // EmbedEditorPage's `embedLanguage`. See apps/h5p-core/src/l10n-vi.json.
    const urlLang = searchParams.get('lang');
    const embedLanguage = urlLang ? normalizeContentLocale(urlLang) : undefined;
    const contentId = urlMetaId || urlContentId || 'h5p-embedded-player';

    // Load content from API by ID
    const loadContentFromApi = useCallback(
        async (id: string) => {
            // Ensure API is configured with public key before loading
            if (urlBaseUrl && urlPublicKey) {
                const h5pUrl = urlH5pUrl || urlBaseUrl;
                console.log('[H5PEmbedPlayer] Configuring API before load:', {
                    baseUrl: urlBaseUrl,
                    h5pUrl,
                    hasPublicKey: !!urlPublicKey,
                });
                const apiConfig: Parameters<typeof configureApi>[0] = {
                    baseUrl: urlBaseUrl,
                    h5pUrl: h5pUrl,
                };
                apiConfig.getPublicKey = () => urlPublicKey;
                configureApi(apiConfig);
            }

            try {
                const apiContent = await fetchContent(id);
                console.log('[H5PEmbedPlayer] Loaded content from API:', {
                    id: apiContent.id,
                    contentType: apiContent.content_type,
                    library: apiContent.content_type?.library,
                });

                const h5pData = extractH5PContentFromData(apiContent.data);

                if (!h5pData) {
                    throw new Error('This content does not contain H5P data');
                }

                // Get library from content_type or from the h5p data itself
                const h5pLibrary = apiContent.content_type?.library || h5pData.library;

                if (!h5pLibrary) {
                    throw new Error('H5P library not found in content type or content data');
                }

                console.log('[H5PEmbedPlayer] Using H5P library:', h5pLibrary);

                const h5pContent: H5PContentItem = {
                    id: apiContent.id,
                    h5pLibrary: h5pLibrary,
                    h5p: h5pData,
                };

                setContent(h5pContent);
                setIsInitialized(true);
            } catch (err) {
                console.error('Failed to load content from API:', err);
                setError(err instanceof Error ? err.message : 'Failed to load content');
                sendToParent(H5P_PLAYER_MESSAGE_TYPES.ERROR, contentId, {
                    message: err instanceof Error ? err.message : 'Failed to load content',
                    error: err,
                });
                setIsInitialized(true);
            }
        },
        [contentId, urlBaseUrl, urlH5pUrl, urlPublicKey]
    );

    // Load content from API by meta_id
    const loadContentByMetaId = useCallback(
        async (metaId: string) => {
            // Ensure API is configured with public key before loading
            if (urlBaseUrl && urlPublicKey) {
                const h5pUrl = urlH5pUrl || urlBaseUrl;
                console.log('[H5PEmbedPlayer] Configuring API before load by metaId:', {
                    baseUrl: urlBaseUrl,
                    h5pUrl,
                    hasPublicKey: !!urlPublicKey,
                });
                const apiConfig: Parameters<typeof configureApi>[0] = {
                    baseUrl: urlBaseUrl,
                    h5pUrl: h5pUrl,
                };
                apiConfig.getPublicKey = () => urlPublicKey;
                configureApi(apiConfig);
            }

            try {
                console.log('[H5PEmbedPlayer] Loading content by metaId:', metaId);
                const apiContent = await contentService.getContentByMetaId(metaId);
                console.log('[H5PEmbedPlayer] Loaded content from API by metaId:', {
                    id: apiContent.id,
                    metaId: apiContent.meta_id,
                    contentType: apiContent.content_type,
                    library: apiContent.content_type?.library,
                });

                const h5pData = extractH5PContentFromData(apiContent.data);

                if (!h5pData) {
                    throw new Error('This content does not contain H5P data');
                }

                // Get library from content_type or from the h5p data itself
                const h5pLibrary = apiContent.content_type?.library || h5pData.library;

                if (!h5pLibrary) {
                    throw new Error('H5P library not found in content type or content data');
                }

                console.log('[H5PEmbedPlayer] Using H5P library:', h5pLibrary);

                const h5pContent: H5PContentItem = {
                    id: apiContent.id,
                    h5pLibrary: h5pLibrary,
                    h5p: h5pData,
                };

                setContent(h5pContent);
                setIsInitialized(true);
            } catch (err) {
                console.error('Failed to load content by metaId:', err);
                setError(err instanceof Error ? err.message : 'Failed to load content');
                sendToParent(H5P_PLAYER_MESSAGE_TYPES.ERROR, contentId, {
                    message: err instanceof Error ? err.message : 'Failed to load content',
                    error: err,
                });
                setIsInitialized(true);
            }
        },
        [contentId, urlBaseUrl, urlH5pUrl, urlPublicKey]
    );

    // Configure API from URL params
    useEffect(() => {
        // Debug logging
        console.log('[H5PEmbedPlayer] Configuration:', {
            urlBaseUrl,
            urlH5pUrl,
            urlLibrary,
            urlMetaId,
            urlContentId,
            urlPublicKey: urlPublicKey ? '***' : '(not set)',
            VITE_API_URL: import.meta.env.VITE_API_URL,
            VITE_H5P_URL: import.meta.env.VITE_H5P_URL,
        });

        if (urlBaseUrl && urlPublicKey) {
            // API mode requires publicKey for authentication
            const h5pUrl = urlH5pUrl || urlBaseUrl;
            console.log('[H5PEmbedPlayer] Configuring API with authentication:', {
                baseUrl: urlBaseUrl,
                h5pUrl,
                hasPublicKey: !!urlPublicKey,
            });

            const apiConfig: Parameters<typeof configureApi>[0] = {
                baseUrl: urlBaseUrl,
                h5pUrl: h5pUrl,
            };
            apiConfig.getPublicKey = () => urlPublicKey;
            configureApi(apiConfig);
            setApiMode(true);

            // Priority: metaId > contentId
            if (urlMetaId) {
                loadContentByMetaId(urlMetaId);
            } else if (urlContentId) {
                loadContentFromApi(urlContentId);
            }
        } else if (urlBaseUrl && !urlPublicKey) {
            // API URL provided but no authentication - show error
            console.warn(
                '[H5PEmbedPlayer] API URL provided but publicKey is missing. API mode disabled.'
            );
            setError('Public key is required for API access. Add ?publicKey=xxx to the URL.');
        }

        // Set library from URL if provided (for postMessage mode)
        if (urlLibrary) {
            setLibrary(urlLibrary);
        }
    }, [
        urlBaseUrl,
        urlH5pUrl,
        urlLibrary,
        urlMetaId,
        urlContentId,
        urlPublicKey,
        loadContentFromApi,
        loadContentByMetaId,
    ]);

    // Report height changes to parent
    const reportHeight = useCallback(() => {
        const height = document.body.scrollHeight;
        sendToParent(H5P_PLAYER_MESSAGE_TYPES.HEIGHT_CHANGE, contentId, { height });
    }, [contentId]);

    // Handle player loaded
    const handlePlayerLoaded = useCallback(() => {
        sendToParent(H5P_PLAYER_MESSAGE_TYPES.LOADED, contentId);
        setTimeout(reportHeight, 100);
    }, [contentId, reportHeight]);

    // Handle answer
    const handleAnswer = useCallback(
        (answer: unknown) => {
            sendToParent(H5P_PLAYER_MESSAGE_TYPES.ANSWER, contentId, { answer });
        },
        [contentId]
    );

    // Handle submit
    const handleSubmit = useCallback(
        (result: unknown) => {
            sendToParent(H5P_PLAYER_MESSAGE_TYPES.SUBMIT, contentId, { result });
        },
        [contentId]
    );

    // Handle complete
    const handleComplete = useCallback(
        (result: unknown) => {
            sendToParent(H5P_PLAYER_MESSAGE_TYPES.COMPLETE, contentId, { result });
        },
        [contentId]
    );

    // Handle error
    const handleError = useCallback(
        (errorMessage: string) => {
            setError(errorMessage);
            sendToParent(H5P_PLAYER_MESSAGE_TYPES.ERROR, contentId, { message: errorMessage });
        },
        [contentId]
    );

    // Listen for messages from parent
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // EAA-1810 (G8): only the allowlisted host may drive this embed.
            // Checked on event.origin, never the payload shape -- a hostile
            // framer can put any source/type it likes in the body.
            if (!isTrustedMessage(event)) return;
            const message = event.data as ParentMessage;

            // Filter messages not intended for this player
            if (message.contentId && message.contentId !== contentId) {
                return;
            }

            switch (message.type) {
                case H5P_PLAYER_MESSAGE_TYPES.INIT:
                    if (message.payload) {
                        const payload = message.payload;

                        // Configure API if provided (requires publicKey)
                        if (payload.apiConfig?.baseUrl && payload.apiConfig?.publicKey) {
                            const h5pUrl = payload.apiConfig.h5pUrl || payload.apiConfig.baseUrl;
                            const apiConfig: Parameters<typeof configureApi>[0] = {
                                baseUrl: payload.apiConfig.baseUrl,
                                h5pUrl: h5pUrl,
                            };
                            apiConfig.getPublicKey = () => payload.apiConfig!.publicKey!;
                            configureApi(apiConfig);
                            setApiMode(true);

                            // Priority: metaId > contentId
                            if (payload.metaId) {
                                loadContentByMetaId(payload.metaId);
                                return;
                            }
                            if (payload.contentId) {
                                loadContentFromApi(payload.contentId);
                                return;
                            }
                        } else if (payload.apiConfig?.baseUrl && !payload.apiConfig?.publicKey) {
                            // API config provided but missing authentication
                            setError('Public key is required for API access.');
                        }

                        // Set content or library
                        if (payload.content) {
                            setContent(payload.content);
                        }
                        if (payload.library) {
                            setLibrary(payload.library);
                        }
                        if (payload.readOnly !== undefined) {
                            setReadOnly(payload.readOnly);
                        }

                        setIsInitialized(true);
                        setError(null);
                    }
                    break;

                case H5P_PLAYER_MESSAGE_TYPES.RESET:
                    // Reset player state - increment key to force re-render
                    setKey((k) => k + 1);
                    break;

                case H5P_PLAYER_MESSAGE_TYPES.CONFIG_UPDATE:
                    if (message.payload) {
                        if (message.payload.readOnly !== undefined) {
                            setReadOnly(message.payload.readOnly);
                        }
                    }
                    break;
            }
        };

        window.addEventListener('message', handleMessage);

        // Signal ready to parent
        sendToParent(H5P_PLAYER_MESSAGE_TYPES.READY, contentId);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [contentId, loadContentFromApi, loadContentByMetaId]);

    // Observe body size changes and report height
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            reportHeight();
        });

        resizeObserver.observe(document.body);
        setTimeout(reportHeight, 100);

        return () => resizeObserver.disconnect();
    }, [reportHeight]);

    // Report height when content changes
    useEffect(() => {
        if (isInitialized) {
            setTimeout(reportHeight, 100);
        }
    }, [content, isInitialized, reportHeight]);

    // hideSubmitButton: inject CSS to hide the H5P "Check" / "Check answer" button
    useEffect(() => {
        if (!hideSubmitButton) {
            return undefined;
        }
        const style = document.createElement('style');
        style.setAttribute('data-hide-submit', 'true');
        style.textContent = `
            /* Hide H5P "Check" / "Check answer" button */
            .h5p-question-check-answer, .h5p-question-buttons,
            .h5p-question-buttons .h5p-question-check-answer {
                display: none !important;
                margin-bottom: 0 !important;
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, [hideSubmitButton]);

    if (!isInitialized && !apiMode) {
        return (
            <div
                className="h5p-embed-player-waiting"
                style={{
                    padding: 24,
                    textAlign: 'center',
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                Waiting for initialization...
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="h5p-embed-player-error"
                style={{
                    padding: 24,
                    textAlign: 'center',
                    minHeight: 200,
                    color: 'red',
                }}
            >
                {error}
            </div>
        );
    }

    if (!content && !urlMetaId && !urlContentId) {
        return (
            <div
                className="h5p-embed-player-no-content"
                style={{
                    padding: 24,
                    textAlign: 'center',
                    minHeight: 200,
                }}
            >
                {apiMode
                    ? 'Loading content...'
                    : 'No content provided. Provide metaId or contentId URL param or send content via H5P_PLAYER_INIT message.'}
            </div>
        );
    }

    return (
        <div className="h5p-embed-player-page" style={{ padding: 0 }}>
            <H5PPlayer
                key={key}
                content={content || undefined}
                contentId={urlContentId || content?.id}
                h5pLibrary={library}
                readOnly={readOnly}
                onAnswer={handleAnswer}
                onSubmit={handleSubmit}
                onComplete={handleComplete}
                onError={handleError}
                onLoaded={handlePlayerLoaded}
                language={embedLanguage}
            />
        </div>
    );
};

export default H5PEmbedPlayerPage;
